import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Button,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import AudioPlayer from './AudioPlayer';
import VoiceRecorder from './VoiceRecorder';

export interface DiscoverProfile {
  user_id: string;
  photo_url: string;
  voice_recording_url: string;
  tags: string[];
  distance_km: number | null;
  prob_face_liked?: number;
  prob_voice_liked?: number;
  pending_voice_message_url: string | null;
}

export type ReviewStep = 'photo' | 'voice' | 'voice_feedback' | 'action';

interface ProfileCardProps {
  profile: DiscoverProfile;
  step: ReviewStep;
  onListenComplete: () => void;
  onVoiceFeedback: (liked: boolean) => void;
  onLike: () => void;
  onSkip: () => void;
  onSendVoice: (audioBlob: Blob) => Promise<void>;
  onBlock: (userId: string) => Promise<void>;
  onReport: (userId: string, reason: string) => Promise<void>;
  sending: boolean;
  learning?: boolean;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile, step, onListenComplete, onVoiceFeedback, onLike, onSkip,
  onSendVoice, onBlock, onReport, sending, learning,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportSuccess, setReportSuccess] = useState('');

  return (
    <Box>
      {/* ── Photo ── */}
      <Box sx={{ position: 'relative', borderRadius: '2px', overflow: 'hidden', border: '1px solid', borderColor: learning ? '#93C5FD' : 'divider' }}>
        <Box
          component="img"
          src={profile.photo_url}
          alt=""
          sx={{ display: 'block', width: '100%', height: { xs: 360, sm: 440 }, objectFit: 'cover', objectPosition: 'center top' }}
        />
        {/* Overlay: distance + menu */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', p: 1 }}>
          {profile.distance_km !== null ? (
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', color: '#faf8f5', backgroundColor: 'rgba(26,22,20,0.5)', px: 0.8, py: 0.2, borderRadius: '2px' }}>
              {profile.distance_km} km
            </Typography>
          ) : <Box />}
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ width: 28, height: 28, backgroundColor: 'rgba(255,255,255,0.7)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }}
          >
            <MoreHorizIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        {/* Overlay: bottom — tags + compatibility */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {/* Tags */}
          {profile.tags.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
              {profile.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" sx={{ backgroundColor: 'rgba(26,22,20,0.6)', color: '#faf8f5', fontSize: '0.6rem', height: 20, backdropFilter: 'blur(4px)' }} />
              ))}
            </Box>
          ) : <Box />}
          {/* Compatibility */}
          {(profile.prob_face_liked != null || profile.prob_voice_liked != null) && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {profile.prob_face_liked != null && (
                <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.5rem', color: '#faf8f5', backgroundColor: 'rgba(26,22,20,0.5)', px: 0.5, py: 0.15, borderRadius: '2px' }}>
                  {Math.round(profile.prob_face_liked * 100)}%
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); onBlock(profile.user_id); }} sx={{ fontSize: '0.8rem' }}>Block</MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); setReportOpen(true); }} sx={{ fontSize: '0.8rem' }}>Report</MenuItem>
        </Menu>
      </Box>

      {/* ── Steps ── */}
      <Box sx={{ mt: 1.5 }}>
        {/* Photo step: listen button */}
        {step === 'photo' && (
          <ActionRow
            onClick={onListenComplete}
            icon={<VolumeUpIcon sx={{ fontSize: 14, color: '#faf8f5' }} />}
            label="Listen to their voice"
            accent
          />
        )}

        {/* Voice step: player + thumbs on one row */}
        {(step === 'voice' || step === 'voice_feedback') && (
          <Box>
            {profile.pending_voice_message_url && (
              <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: '2px', p: 1.5, mb: 1, backgroundColor: 'rgba(192,57,43,0.03)' }}>
                <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', color: 'primary.main', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Voice message for you
                </Typography>
                <AudioPlayer src={profile.pending_voice_message_url} />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: '2px', px: 1.5, py: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AudioPlayer src={profile.voice_recording_url} autoPlay={step === 'voice'} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Box
                  onClick={() => onVoiceFeedback(true)}
                  sx={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid', borderColor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'success.main', transition: 'all 0.2s', '&:hover': { backgroundColor: 'success.light' } }}
                >
                  <ThumbUpOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
                <Box
                  onClick={() => onVoiceFeedback(false)}
                  sx={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'text.disabled', transition: 'all 0.2s', '&:hover': { borderColor: 'text.primary', color: 'text.primary' } }}
                >
                  <ThumbDownOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Action step: like / voice message / skip */}
        {step === 'action' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <ActionRow
                onClick={onLike}
                icon={<FavoriteIcon sx={{ fontSize: 14, color: '#faf8f5' }} />}
                label="Like"
                accent
                disabled={sending}
                sx={{ flex: 2, justifyContent: 'center' }}
              />
              <ActionRow
                onClick={onSkip}
                icon={<CloseIcon sx={{ fontSize: 14 }} />}
                label="Skip"
                disabled={sending}
                sx={{ flex: 1, justifyContent: 'center' }}
              />
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '2px', p: 1.5 }}>
              <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', color: 'text.disabled', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Or send a voice message
              </Typography>
              <VoiceRecorder onUpload={onSendVoice} uploading={sending} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Report dialog */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem' }}>Report</DialogTitle>
        <DialogContent>
          {reportSuccess && <Alert severity="success" sx={{ mb: 1 }}>{reportSuccess}</Alert>}
          <TextField
            fullWidth multiline rows={2} size="small"
            placeholder="Why are you reporting this profile?"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setReportOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" color="error" disabled={!reportReason.trim() || reportSending} onClick={async () => {
            if (!reportReason.trim()) return;
            setReportSending(true);
            try { await onReport(profile.user_id, reportReason.trim()); setReportOpen(false); setReportReason(''); }
            finally { setReportSending(false); }
          }}>
            {reportSending ? 'Sending…' : 'Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function ActionRow({ onClick, icon, label, accent, disabled, sx }: {
  onClick: () => void; label: string; icon?: React.ReactNode; accent?: boolean; disabled?: boolean; sx?: Record<string, any>;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.8,
        px: 2, py: 1, cursor: disabled ? 'default' : 'pointer',
        border: '1px solid', borderColor: accent ? 'text.primary' : 'divider',
        borderRadius: '2px', opacity: disabled ? 0.4 : 1,
        backgroundColor: accent ? 'text.primary' : 'transparent',
        color: accent ? '#faf8f5' : 'text.disabled',
        transition: 'all 0.2s',
        '&:hover': disabled ? {} : { borderColor: accent ? 'text.primary' : 'primary.main', color: accent ? '#faf8f5' : 'primary.main' },
        ...sx,
      }}
    >
      {icon}
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.72rem', fontWeight: 500 }}>{label}</Typography>
    </Box>
  );
}

export default ProfileCard;
